import { useState, useEffect, useRef } from "react";
import { fetcher } from "../../../utils/fetcher";
import { getAllNotes } from "../../../api/notes/getAll";
import useAuthStore from "../../../store/authStore";
import "./CreateNewNoteModal.scss";
import "../../../components/ModalContent/ModalComponents.scss";
import { useTranslation } from "react-i18next";

function CreateNewNoteModal({ onClose, onNoteCreated }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [noteGroups, setNoteGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showTitleError, setShowTitleError] = useState(false);

  const currentProject = useAuthStore((state) => state.currentProject);
  const inputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (currentProject?._id) {
      setIsLoading(true);
      getAllNotes(false, currentProject._id)
        .then((data) => {
          setNoteGroups(data || []);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching note groups:", err);
          setNoteGroups([]);
          setIsLoading(false);
        });
    }
  }, [currentProject?._id]);

  const handleCreateNote = async () => {
    if (!title.trim()) {
      setShowTitleError(true);
      return;
    }
    if (!selectedGroup) return;

    setIsCreating(true);
    try {
      const newNote = await fetcher.post("/api/notes", {
        groupId: selectedGroup.id,
        title: title.trim(),
        content: "",
        color: selectedGroup.color,
        isActive: true,
      });

      if (onNoteCreated) {
        onNoteCreated(newNote, selectedGroup);
      }
    } catch (error) {
      console.error("Error creating note:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    setTitle("");
    setSelectedGroup(null);
    setShowTitleError(false);
    onClose();
  };

  const isCreateDisabled = !title.trim() || !selectedGroup || isCreating;

  return (
    <div className="create-new-note-modal">
      <div className="modal-form">
        <div className="modal-form-title">{t("common.title")}</div>
        <input
          ref={inputRef}
          type="text"
          className="modal-text-input"
          placeholder={t("tasks.links.noteTitlePlaceholder")}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (showTitleError && e.target.value.trim()) {
              setShowTitleError(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !isCreateDisabled) {
              handleCreateNote();
            }
          }}
        />
        {showTitleError && (
          <div className="modal-helper-text">
            {t("tasks.links.titleRequired")}
          </div>
        )}

        <div className="modal-form-title">{t("tasks.links.noteGroup")}</div>
        <div className="notegroups-list">
          {isLoading ? (
            <div className="loading-state">
              {t("tasks.links.loadingNoteGroups")}
            </div>
          ) : noteGroups.length === 0 ? (
            <div className="empty-state">{t("tasks.links.noNoteGroups")}</div>
          ) : (
            noteGroups.map((group) => (
              <div
                key={group._id}
                className={`notegroup-item ${selectedGroup?._id === group._id ? "selected" : ""}`}
                onClick={() => setSelectedGroup(group)}
                style={{ "--notegroup-color": group.color }}
              >
                <div
                  className="notegroup-color-bar"
                  style={{ backgroundColor: group.color }}
                />
                <span className="notegroup-name">{group.name}</span>
                <span className="notegroup-count">
                  ({group.notes?.length || 0})
                </span>
                {selectedGroup?._id === group._id && (
                  <span className="material-symbols-outlined check-icon">
                    check
                  </span>
                )}
              </div>
            ))
          )}
        </div>

        <div className="modal-form-buttons">
          <button
            className="modal-button cancel"
            onClick={handleCancel}
            disabled={isCreating}
          >
            {t("common.cancel")}
          </button>
          <button
            className="modal-button save"
            onClick={handleCreateNote}
            disabled={isCreateDisabled}
          >
            {isCreating ? t("common.creating") : t("common.create")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateNewNoteModal;
